import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useWebSocket } from './websocket-context'

const NotificationContext = createContext()

export const NotificationProvider = ({ children }) => {
  const [messageNotification, setMessageNotification] = useState({})
  const [examNotification, setExamNotification] = useState({})
  const [visitNotification, setVisitNotification] = useState({})
  const [patientNotification, setPatientNotification] = useState({})
  const [claimNotification, setClaimNotification] = useState({})
  const [isReadFrom, setIsReadFrom] = useState({})

  const { subscribeToResponses } = useWebSocket()

  // Process WebSocket messages and update notifications
  useEffect(() => {
    const unsubscribe = subscribeToResponses((data) => {
      if (!data || !data.method) return

      if (
        data.method === "examCreated" ||
        data.method === "examClosed" ||
        data.method === "examUpdated"
      ) {
        setExamNotification(data.params || {})
      }

      if (data.method === "visitCreated" || data.method === "visitOpen") {
        setVisitNotification(data.params || {})
      }

      if (data.method === "claimsUpdate") {
        setClaimNotification(data.params || {})
      }

      if (data.method === "isReadFrom") {
        setIsReadFrom(data.params || {})
      }

      if (
        data.method === "patientCreated" ||
        data.method === "patientDeleted" ||
        data.method === "patientUpdated" ||
        data.method === "userCreated"
      ) {
        setPatientNotification(data.params || {})
      }

      if (data.method === "chat") {
        setMessageNotification(data.params || {})
      }
    })

    return unsubscribe
  }, [subscribeToResponses])

  // Methods to manually trigger notifications (for testing or external events)
  const notifyMessage = useCallback((data) => setMessageNotification(data), [])
  const notifyExam = useCallback((data) => setExamNotification(data), [])
  const notifyVisit = useCallback((data) => setVisitNotification(data), [])
  const notifyPatient = useCallback((data) => setPatientNotification(data), [])
  const notifyClaim = useCallback((data) => setClaimNotification(data), [])
  const notifyIsReadFrom = useCallback((data) => setIsReadFrom(data), [])

  const value = {
    // State
    messageNotification,
    examNotification,
    visitNotification,
    patientNotification,
    claimNotification,
    isReadFrom,

    // Setters (equivalent to .next() in BehaviorSubject)
    notifyMessage,
    notifyExam,
    notifyVisit,
    notifyPatient,
    notifyClaim,
    notifyIsReadFrom,

    // For backward compatibility with existing patterns
    setMessageNotification,
    setExamNotification,
    setVisitNotification,
    setPatientNotification,
    setClaimNotification,
    setIsReadFrom
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
